import type React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import type {
  Role,
  CrewMember,
  AssignmentData,
  Stream,
} from "../types/crew-assignment";
import AssignmentCell from "./AssignmentCell";
import SubmitSection from "./SubmitSection";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "70vh",
  "& .MuiTableHead-root": {
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
  "& .role-column": {
    position: "sticky",
    left: 0,
    backgroundColor: theme.palette.background.paper,
    zIndex: 5,
    borderRight: `2px solid ${theme.palette.divider}`,
    minWidth: 200,
  },
  "& .event-column": {
    minWidth: 250,
    padding: theme.spacing(1),
  },
}));

const RoleChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.25),
  fontSize: "0.75rem",
  height: 24,
}));

interface AssignmentGridProps {
  selectedEvents: Stream[];
  roles: Role[];
  assignments: AssignmentData;
  totalAssignments: number;
  getAssignment: (eventId: number, roleId: number) => CrewMember[];

  updateAssignment: (
    eventId: number,
    roleId: number,
    crewMembers: CrewMember[]
  ) => void;
  onSubmit: () => void;
}

const AssignmentGrid: React.FC<AssignmentGridProps> = ({
  selectedEvents,
  roles,
  totalAssignments,
  getAssignment,
  updateAssignment,
  onSubmit,
}) => {
  const reduxTheme = useSelector((state: RootState) => state.theme);

  return (
    <Card sx={{ boxShadow: 0 }}>
      <CardContent>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Role Assignments
          </Typography>
          <SubmitSection
            totalAssignments={totalAssignments}
            onSubmit={onSubmit}
            showSection={selectedEvents.length > 0}
          />
        </Box>

        {/* Table */}
        <StyledTableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="role-column">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Role
                  </Typography>
                </TableCell>

                {selectedEvents.map((stream) => {
                  const assignedCount = roles.reduce(
                    (sum, role) =>
                      sum +
                      (getAssignment(Number(stream.id), Number(role.id))
                        ?.length || 0),
                    0
                  );

                  const deceasedName =
                    stream.booking.deceased?.[0] !== undefined
                      ? `${stream.booking.deceased[0].first_name} ${stream.booking.deceased[0].surname}`
                      : "No Deceased";

                  const venueName =
                    stream.service?.venue?.name ||
                    stream.booking.services?.[0]?.venue?.name ||
                    "N/A";

                  return (
                    <TableCell key={stream.id} className="event-column">
                      <Typography variant="subtitle2" fontWeight="bold">
                        {deceasedName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Venue: {venueName}
                      </Typography>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Assigned: {assignedCount} crew
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  {/* Role column */}
                  <TableCell className="role-column">
                    <Box>
                      <RoleChip
                        label={role.name}
                        style={{
                          backgroundColor: reduxTheme.primary_colors[800],
                          color: reduxTheme.primary_colors[100],
                        }}
                      />
                    </Box>
                  </TableCell>

                  {/* Assignments per stream */}
                  {selectedEvents.map((stream) => (
                    <AssignmentCell
                      key={`${stream.id}-${role.id}`}
                      role={role}
                      event={stream}
                      selectedCrew={
                        getAssignment(Number(stream.id), Number(role.id))[0] ||
                        null
                      }
                      onCrewChange={(crewMember) =>
                        updateAssignment(
                          Number(stream.id),
                          Number(role.id),
                          crewMember ? [crewMember] : []
                        )
                      }
                    />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </CardContent>
    </Card>
  );
};

export default AssignmentGrid;
